import { Box, Button, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ArrowRightOutlined, SwapOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useTradeClient } from '../../../../client/client-hook';
import LoadingButton from '../../../../components/@extended/LoadingButton';
import { focusRingSx, primarySx } from '../../../../components/ui/styleKit';
import { MONO_FONT } from '../../../../components/ui/tokens';
import useAuth from '../../../../hooks/useAuth';
import { DispatchSnackbarService } from '../../../../services';
import { useDispatch } from '../../../../store';
import { ListSymbolMappingsReply, Mapping } from '../../../../proto/trade-copier_pb';
import { ink } from '../../../mcp/SignalDesk';
import { useSimulateMeta, useSimulateResult, useSimulateState } from '../SimulateTradeContext';
import { SIMULATE_COPY } from '../content';
import { SimulateCallout } from './SimulateCallout';

type Decision = 'idle' | 'pending' | 'persisted' | 'skipped';
const { mapping } = SIMULATE_COPY;

export const PersistMappingPrompt = () => {
  const theme = useTheme();
  const { status, data } = useSimulateResult();
  const { customSlaveSymbol, masterOrder } = useSimulateState();
  const { slaveId } = useSimulateMeta();
  const { user } = useAuth();
  const tradeClient = useTradeClient();
  const dispatch = useDispatch();

  const [existing, setExisting] = useState<Mapping.AsObject | null>(null);
  const [decision, setDecision] = useState<Decision>('idle');
  const [busy, setBusy] = useState(false);

  const masterSymbol = masterOrder.symbol.trim();
  const userOverride = customSlaveSymbol.trim();
  const resolvedSlaveSymbol = (data?.symbol ?? '').trim();
  const customSymbol = userOverride || resolvedSlaveSymbol;
  const meaningful = Boolean(userOverride) || (Boolean(resolvedSlaveSymbol) && resolvedSlaveSymbol !== masterSymbol);
  const shouldShow = status === 'success' && Boolean(slaveId) && Boolean(masterSymbol) && Boolean(customSymbol) && meaningful;

  useEffect(() => {
    const userId = user?.tcId;
    if (!shouldShow || !userId || !slaveId) return;
    let cancelled = false;
    setDecision('idle');
    setExisting(null);

    tradeClient()
      .then((client) => client.listSymbolMappings({ userId, slaveId }))
      .then((res) => {
        if (cancelled) return;
        const list = (res as ListSymbolMappingsReply.AsObject).mappingsList ?? [];
        const found = list.find((m) => m.master === masterSymbol) ?? null;
        setExisting(found);
        if (found && found.name === customSymbol) {
          setDecision('persisted');
        } else {
          setDecision('pending');
        }
      })
      .catch(() => {
        if (cancelled) return;
        setDecision('pending');
      });

    return () => {
      cancelled = true;
    };
  }, [shouldShow, slaveId, user?.tcId, masterSymbol, customSymbol, tradeClient]);

  if (!shouldShow || decision === 'idle' || decision === 'persisted' || decision === 'skipped') {
    return null;
  }

  const isUpdate = Boolean(existing);
  const service = new DispatchSnackbarService();

  const persist = async () => {
    if (!user?.tcId || !slaveId) return;
    setBusy(true);
    try {
      const client = await tradeClient();
      if (existing) {
        await client.editSymbolMapping({
          userId: user.tcId,
          slaveId,
          symbolId: existing.id,
          name: customSymbol
        });
      } else {
        await client.addSymbolMapping({
          userId: user.tcId,
          slaveId,
          mappingsList: [{ name: customSymbol, master: masterSymbol }]
        });
      }
      service.dispatchSnackbar(dispatch, mapping.saved, 'alert', 'success', 3000);
      setDecision('persisted');
    } catch (err: any) {
      service.dispatchSnackbar(dispatch, err?.message ?? mapping.failed, 'alert', 'error', 5000);
    } finally {
      setBusy(false);
    }
  };

  return (
    <SimulateCallout
      compact
      tone="primary"
      icon={<SwapOutlined />}
      title={isUpdate ? mapping.updateTitle : mapping.saveTitle}
      caption={
        <Stack spacing={0.25} sx={{ mt: 0.25 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ fontFamily: MONO_FONT, fontWeight: 600, color: 'text.primary' }}>
            <span>{masterSymbol}</span>
            <ArrowRightOutlined style={{ fontSize: 11 }} />
            <Box component="span" sx={{ color: ink(theme) }}>
              {customSymbol}
            </Box>
          </Stack>
          <span>{mapping.explain(masterSymbol, customSymbol)}</span>
        </Stack>
      }
      aside={
        <Stack direction="row" spacing={1}>
          <Button variant="text" color="inherit" disabled={busy} onClick={() => setDecision('skipped')} sx={focusRingSx(theme)}>
            {mapping.dismiss}
          </Button>
          <LoadingButton variant="contained" loading={busy} onClick={persist} sx={primarySx(theme)}>
            {isUpdate ? mapping.update : mapping.save}
          </LoadingButton>
        </Stack>
      }
    />
  );
};
